import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import HermesChat from '@/components/platform-admin/HermesChat';
import { ArrowLeft, Info } from 'lucide-react';

const HotelEnrollmentPage = () => {
  return (
    <div className="min-h-screen bg-background admin-theme">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/platform-admin">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            </Button>
            <h1 className="text-lg font-bold heading-font">Hotel Enrollment</h1>
            <div className="w-24" /> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Instructions */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="heading-font flex items-center gap-2">
                  <Info className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
                  Enrollment Guide
                </CardTitle>
                <CardDescription>
                  Hermes will guide you through the enrollment process
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2">Required Information:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Hotel Name</li>
                    <li>Owner Full Name</li>
                    <li>Contact Email</li>
                    <li>Treasury Wallet Address (0x...)</li>
                    <li>Unique Hotel ID</li>
                  </ul>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold text-sm mb-2">What Happens After:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Hotel receives initial HERMES tokens</li>
                    <li>Tier assigned based on score (Bronze = 600)</li>
                    <li>3 AI agent wallets created (Bitsy, Marina, Sage)</li>
                    <li>Hotel can access blockchain features</li>
                  </ul>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold text-sm mb-2">Next Steps for Hotel:</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Fund agent wallets from treasury</li>
                    <li>Configure agent policies</li>
                    <li>Enable desired blockchain features</li>
                    <li>Review reputation stake dashboard</li>
                  </ol>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-900">
                    <strong>Tip:</strong> Start by telling Hermes "Enroll new hotel" or provide the hotel name directly.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Example Prompts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm heading-font">Example Prompts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="p-2 bg-muted rounded">
                    <code className="text-xs">"Enroll Grand Plaza Hotel"</code>
                  </div>
                  <div className="p-2 bg-muted rounded">
                    <code className="text-xs">"Register a new hotel"</code>
                  </div>
                  <div className="p-2 bg-muted rounded">
                    <code className="text-xs">"Add hotel to platform"</code>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Hermes Chat */}
          <div className="lg:col-span-2">
            <div className="h-[800px]">
              <HermesChat initialMessage="I'm ready to enroll a new hotel. What information do you need?" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HotelEnrollmentPage;
